import { Component, useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Sentry } from '@/lib/sentry';
import i18n from '@/i18n';
import {
  isChunkLoadError,
  RELOAD_FLAG,
  reloadToLatest,
  tryRecoverFromChunkError,
} from '@/lib/chunk-recovery';

/**
 * What a stale-chunk error shows instead of a crash card. A new build shipped
 * while the tab was open; that is not a bug and must never read as one.
 *
 * - default — "Updating…" while the automatic reload runs. If the page is
 *   somehow still here after a few seconds (a beforeunload prompt was
 *   cancelled, the network stalled) a Reload button appears.
 * - `failed` — automatic reloads are used up; almost always a dropped
 *   connection. Offers a reload, nothing alarming.
 *
 * Plain markup and `i18n.t` because AppErrorBoundary renders it above every
 * provider.
 */
export function ChunkRecoveryScreen({ failed = false }: { failed?: boolean }) {
  const [showReload, setShowReload] = useState(failed);
  useEffect(() => {
    if (failed) return;
    const id = window.setTimeout(() => setShowReload(true), 8_000);
    return () => window.clearTimeout(id);
  }, [failed]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-0 p-6 text-center text-body text-muted"
    >
      <p>
        {failed
          ? i18n.t('errors.page.updateFailed')
          : i18n.t('errors.page.updating')}
      </p>
      {showReload && (
        <button
          type="button"
          onClick={reloadToLatest}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-5 text-body font-semibold text-white transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <RefreshCw className="size-4" aria-hidden />
          {i18n.t('errors.page.reload')}
        </button>
      )}
    </div>
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  recovering: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, recovering: false };

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error)) {
      const recovering = tryRecoverFromChunkError();
      return { error, recovering };
    }
    return { error, recovering: false };
  }

  componentDidCatch(error: Error) {
    if (typeof window === 'undefined' || isChunkLoadError(error)) return;
    Sentry.captureException(error, { tags: { boundary: 'AppErrorBoundary' } });
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
    window.location.reload();
  };

  render() {
    const { error, recovering } = this.state;
    if (!error) return this.props.children;
    if (isChunkLoadError(error)) {
      return <ChunkRecoveryScreen failed={!recovering} />;
    }
    /* This is the LAST resort — it renders above every provider, so it uses
       `i18n.t` rather than the hook and plain markup rather than the design
       system: whatever broke may well be one of the things `ds` sits on.
       The in-app version, which keeps the nav alive, is RouteErrorBoundary. */
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center bg-surface-0 p-6"
      >
        <div className="flex w-full max-w-sm flex-col items-center gap-6 rounded-lg border border-neutral-200 bg-white p-8 text-center">
          <div
            className="flex size-14 items-center justify-center rounded-full bg-danger-50"
            aria-hidden
          >
            <AlertTriangle className="size-7 text-danger-600" />
          </div>
          <div className="space-y-2">
            <h1 className="text-h2 text-foreground">
              {i18n.t('errors.app.title')}
            </h1>
            <p className="text-body text-muted">
              {i18n.t('errors.app.description')}
            </p>
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-5 text-body font-semibold text-white transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <RefreshCw className="size-4" aria-hidden />
            {i18n.t('errors.app.reload')}
          </button>
        </div>
      </div>
    );
  }
}
